import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Calendar, CheckCircle, Circle } from 'lucide-react';
import { motion } from 'framer-motion';

const ReportTypeSelector = ({ selectedType, onTypeChange }) => {
  const { t } = useTranslation();

  const reportTypes = [
    {
      id: 'candidates',
      title: t('report.candidatesReport'),
      subtitle: t('report.candidatesReportDescription'),
      icon: Users,
      palette: {
        ring: 'ring-blue-500/40',
        bg: 'bg-blue-500/10',
        grad: 'from-blue-400/15 to-blue-300/5',
        icon: 'text-blue-600 bg-blue-500/15',
        text: 'text-blue-600',
        dot: 'bg-blue-500 text-white'
      }
    },
    {
      id: 'interviews',
      title: t('report.interviewsReport'),
      subtitle: t('report.interviewsReportDescription'),
      icon: Calendar,
      palette: {
        ring: 'ring-violet-500/40',
        bg: 'bg-violet-500/10',
        grad: 'from-violet-400/15 to-violet-300/5',
        icon: 'text-violet-600 bg-violet-500/15',
        text: 'text-violet-600',
        dot: 'bg-violet-500 text-white'
      }
    }
  ];

  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      {reportTypes.map(({ id, title, subtitle, icon: Icon, palette }) => {
        const active = selectedType === id;
        const dim = selectedType && !active;

        const ring = active ? palette.ring : 'ring-transparent';
        const bg = active ? palette.bg : 'bg-white/80';
        const grad = active ? palette.grad : '';
        const icon = active ? palette.icon : 'text-gray-600 bg-gray-100';
        const text = active ? palette.text : 'text-gray-400';
        const dot = active ? palette.dot : 'bg-gray-200 text-gray-500';

        return (
          <motion.button
            key={id}
            onClick={() => onTypeChange(id)}
            whileHover={{ scale: active ? 1 : 1.015 }}
            whileTap={{ scale: 0.985 }}
            animate={{ opacity: dim ? 0.55 : 1, scale: dim ? 0.97 : 1 }}
            transition={{ duration: 0.25 }}
              className={[
                'relative w-full rounded-2xl p-3 text-left overflow-hidden backdrop-blur-md transition-all duration-300',
              bg,
              ring,
              active
                ? 'border border-transparent shadow-[0_8px_30px_rgba(0,0,0,0.07)]'
                : 'border border-gray-200 shadow-[0_4px_18px_rgba(0,0,0,0.04)] hover:shadow-[0_6px_24px_rgba(0,0,0,0.06)]'
            ].join(' ')}
          >
            {active && (
              <motion.div
                layoutId="card_glow"
                transition={{ duration: 0.25 }}
                className={`absolute inset-0 bg-gradient-to-br ${grad}`}
              />
            )}

            <div className="relative z-10 flex flex-col h-full">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-2 transition-all duration-200 ${icon}`}>
                <Icon size={20} strokeWidth={2.2} />
              </div>

              <h3 className="text-base font-semibold tracking-tight mb-1 text-gray-900" style={{ fontFamily: "'Poppins', sans-serif" }}>{title}</h3>

              <p className="text-xs text-gray-500 leading-relaxed max-w-sm">{subtitle}</p>

              <div className="mt-auto pt-4 flex items-center justify-between">
                <span className={`text-xs font-semibold tracking-wide ${text}`}>
                  {active ? t('report.selected') : t('report.select')}
                </span>

                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${dot}`}>
                  {active ? <CheckCircle size={20} /> : <Circle size={20} />}
                </div>
              </div>
            </div>
          </motion.button>
        );
      })}
    </div>
  );
};

export default ReportTypeSelector;